import { useMemo, useState, useCallback } from "react"
import {
  Map, GraduationCap, Building2, BookOpen, Globe,
  CheckCircle2, ChevronDown, ChevronUp, Star,
} from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { getRoadmapForPath } from "@/data/careerRoadmaps"

const PATH_LABELS = {
  broad_stem_tech:         "STEM & Technology",
  broad_health_medicine:   "Health & Medicine",
  broad_business_finance:  "Business & Finance",
  broad_creative_arts:     "Creative Arts",
  broad_law_social:        "Law & Social Sciences",
  broad_law_governance:    "Law & Governance",
}

// Colour tokens per phase (6 phases × 6 colours)
const PHASE_COLS = [
  { dot: "bg-indigo-500",  ring: "ring-indigo-300",  badge: "bg-indigo-500",  card: "bg-indigo-50 border-indigo-200",  bar: "bg-indigo-500" },
  { dot: "bg-violet-500",  ring: "ring-violet-300",  badge: "bg-violet-500",  card: "bg-violet-50 border-violet-200",  bar: "bg-violet-500" },
  { dot: "bg-teal-500",    ring: "ring-teal-300",    badge: "bg-teal-500",    card: "bg-teal-50 border-teal-200",      bar: "bg-teal-500" },
  { dot: "bg-amber-500",   ring: "ring-amber-300",   badge: "bg-amber-500",   card: "bg-amber-50 border-amber-200",    bar: "bg-amber-500" },
  { dot: "bg-emerald-500", ring: "ring-emerald-300", badge: "bg-emerald-500", card: "bg-emerald-50 border-emerald-200",bar: "bg-emerald-500" },
  { dot: "bg-rose-500",    ring: "ring-rose-300",    badge: "bg-rose-500",    card: "bg-rose-50 border-rose-200",      bar: "bg-rose-500" },
]

// Map student class level → milestone phase index (0-based)
const CLASS_PHASE = {
  JSS1: 0, JSS2: 0, JSS3: 0,
  SS1: 1,  SS2: 1,
  SS3: 2,
}

// ─── Milestone card ─────────────────────────────────────────────────────────────
function MilestoneCard({ milestone, index, isLast, isCurrent, isPast, pathId, careerInterests, actionState, onToggle }) {
  const col = PHASE_COLS[index % PHASE_COLS.length]
  // Start expanded if this is the student's current phase
  const [open, setOpen] = useState(isCurrent)

  const total = milestone.actions.length
  const done  = milestone.actions.filter((_, ai) => actionState?.[`${index}_${ai}`]).length

  return (
    <div className="flex gap-4">
      {/* Timeline spine */}
      <div className="flex flex-col items-center">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-black shadow transition-all ${
            isPast
              ? "bg-slate-300"
              : `${col.dot}${isCurrent ? ` ring-4 ring-offset-2 ${col.ring}` : ""}`
          }`}
        >
          {isPast ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-slate-200 mt-1 mb-1 min-h-[2rem]" />}
      </div>

      {/* Content */}
      <div className="pb-6 flex-1">
        <button
          className={`w-full text-left rounded-2xl border p-4 transition-all ${
            isCurrent
              ? `${col.card} shadow-md`
              : isPast
                ? "bg-slate-50 border-slate-200 opacity-70 hover:opacity-100"
                : "bg-white border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => setOpen(o => !o)}
        >
          <div className="flex items-start justify-between gap-2">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white shrink-0 ${isPast ? "bg-slate-400" : col.badge}`}>
              {milestone.timeframe}
            </span>
            <div className="flex items-center gap-2">
              {isCurrent && (
                <span className="text-xs font-bold text-white bg-indigo-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                  You are here
                </span>
              )}
              {open
                ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
            </div>
          </div>

          <h3 className="font-bold text-slate-800 mt-2 text-sm">{milestone.title}</h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{milestone.description}</p>

          {/* Progress bar */}
          {total > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${col.bar}`}
                  style={{ width: `${(done / total) * 100}%` }}
                />
              </div>
              <span className="text-xs text-slate-400 whitespace-nowrap">{done}/{total}</span>
            </div>
          )}
        </button>

        {/* Expandable action checklist */}
        {open && (
          <div className="mt-2 ml-1 space-y-2">
            {milestone.actions.map((action, ai) => {
              const checked = !!actionState?.[`${index}_${ai}`]
              return (
                <label
                  key={ai}
                  className="flex items-start gap-2.5 bg-white border border-slate-200 rounded-xl p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(pathId, index, ai)}
                    className="mt-0.5 w-4 h-4 accent-indigo-600 shrink-0"
                  />
                  <span className={`text-xs leading-relaxed ${checked ? "line-through text-slate-400" : "text-slate-700"}`}>
                    {action}
                  </span>
                </label>
              )
            })}

            {/* Career-interest callout — only on current phase */}
            {isCurrent && careerInterests.length > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <Star className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  <span className="font-bold">Your interests:</span>{" "}
                  {careerInterests.slice(0, 3).join(", ")} — keep these in mind as you work through these steps.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── University card ────────────────────────────────────────────────────────────
function UniversityCard({ uni }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
          <Building2 className="w-4 h-4 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-800 text-sm leading-tight">{uni.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Globe className="w-3 h-3 text-slate-400" />
            <span className="text-xs text-slate-400">{uni.country}</span>
          </div>
          {uni.cutoffGuide && (
            <span className="inline-block mt-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-semibold">
              {uni.cutoffGuide}
            </span>
          )}
          <div className="mt-2 flex flex-wrap gap-1">
            {uni.programmes.slice(0, 3).map(p => (
              <span key={p} className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                {p}
              </span>
            ))}
            {uni.programmes.length > 3 && (
              <span className="text-xs text-slate-400">+{uni.programmes.length - 3} more</span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {uni.examBodies.map(e => (
              <span key={e} className="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-medium">{e}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────────
export default function CareerRoadmapPage() {
  const { paths, examBody, classLevel, careerInterests } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    return {
      paths:          student?.selectedBroadPaths ?? [],
      examBody:       student?.preferences?.examBody ?? "",
      classLevel:     student?.classLevel ?? "",
      careerInterests: student?.careerInterests ?? [],
    }
  }, [])

  const roadmaps = useMemo(() =>
    paths
      .map(id => ({ id, label: PATH_LABELS[id] ?? id, roadmap: getRoadmapForPath(id) }))
      .filter(r => r.roadmap != null)
  , [paths])

  const [activeTab, setActiveTab]   = useState(roadmaps[0]?.id ?? "")
  const [showAllUnis, setShowAllUnis] = useState(false)

  // Action checklist state — persisted to localStorage
  const [actionState, setActionState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("roadmap_actions") ?? "{}") } catch { return {} }
  })

  const toggleAction = useCallback((pathId, phaseIndex, actionIndex) => {
    setActionState(prev => {
      const key = `${phaseIndex}_${actionIndex}`
      const updated = { ...(prev[pathId] ?? {}), [key]: !prev[pathId]?.[key] }
      const next = { ...prev, [pathId]: updated }
      try { localStorage.setItem("roadmap_actions", JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  const currentPhaseIndex = CLASS_PHASE[classLevel] ?? -1
  const currentRoadmap    = roadmaps.find(r => r.id === activeTab)?.roadmap

  // Per-path action progress
  const pathActionState = actionState[activeTab] ?? {}
  const totalActions    = currentRoadmap?.milestones.reduce((sum, m) => sum + m.actions.length, 0) ?? 0
  const doneActions     = Object.values(pathActionState).filter(Boolean).length

  if (paths.length === 0) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-100">
          <Sidebar />
          <main className="flex-1 lg:ml-64 flex items-center justify-center p-8">
            <div className="text-center space-y-3">
              <Map className="w-12 h-12 text-slate-300 mx-auto" />
              <p className="font-bold text-slate-700">No career paths selected yet.</p>
              <p className="text-slate-500 text-sm">Complete the Career Assessment to get your personalised roadmap.</p>
            </div>
          </main>
        </div>
      </ProtectedRoute>
    )
  }

  const allUnis      = currentRoadmap?.universities ?? []
  const visibleUnis  = examBody && !showAllUnis
    ? allUnis.filter(u => u.examBodies.includes("all") || u.examBodies.includes(examBody))
    : allUnis

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-black text-slate-900">Career Roadmap</h1>
                <p className="text-slate-500 text-sm mt-1">Your step-by-step guide — from secondary school to your first job.</p>
              </div>
              {totalActions > 0 && (
                <div className="shrink-0 text-right">
                  <span className="text-2xl font-black text-indigo-600">
                    {Math.round((doneActions / totalActions) * 100)}%
                  </span>
                  <p className="text-xs text-slate-400 mt-0.5">steps done</p>
                </div>
              )}
            </div>

            {/* Path tabs */}
            {roadmaps.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {roadmaps.map(r => (
                  <button
                    key={r.id}
                    onClick={() => { setActiveTab(r.id); setShowAllUnis(false) }}
                    className={`text-sm font-semibold px-4 py-2 rounded-xl border transition-colors ${
                      activeTab === r.id
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}

            {currentRoadmap && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Milestones */}
                <div className="lg:col-span-7">
                  <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                      <h2 className="font-black text-slate-900">Your Journey</h2>
                    </div>
                    <div>
                      {currentRoadmap.milestones.map((m, i) => (
                        <MilestoneCard
                          key={`${activeTab}_${i}`}
                          milestone={m}
                          index={i}
                          isLast={i === currentRoadmap.milestones.length - 1}
                          isCurrent={currentPhaseIndex === i}
                          isPast={currentPhaseIndex > i}
                          pathId={activeTab}
                          careerInterests={careerInterests}
                          actionState={pathActionState}
                          onToggle={toggleAction}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Universities + tip */}
                <div className="lg:col-span-5 space-y-4">
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <GraduationCap className="w-5 h-5 text-indigo-600" />
                      <h2 className="font-black text-slate-900">Recommended Universities</h2>
                    </div>
                    {examBody && (
                      <p className="text-xs text-slate-400 mb-3">
                        Filtered for <span className="font-bold text-indigo-600">{examBody}</span> students
                        {allUnis.length > visibleUnis.length || showAllUnis ? (
                          <button
                            className="ml-2 text-indigo-400 underline underline-offset-2"
                            onClick={() => setShowAllUnis(v => !v)}
                          >
                            {showAllUnis ? "show filtered" : "show all"}
                          </button>
                        ) : null}
                      </p>
                    )}
                    <div className="space-y-3">
                      {visibleUnis.map((u, i) => (
                        <UniversityCard key={i} uni={u} />
                      ))}
                      {visibleUnis.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">No universities found for your exam body.</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
                    <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1">Pro Tip</p>
                    <p className="text-sm text-indigo-600 leading-relaxed">
                      Start UTME registration early. University cut-off points shift each year — aim 20+ points above the listed guide.
                    </p>
                  </div>
                </div>

              </div>
            )}

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
